'use client';

import { cn, NAV_GLYPH, NAV_ROW, navRowState } from '@routly/ui';
import { ChevronsUpDown, LogOut, SunMoon, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

/**
 * The foot of the rail: the appearance, and who you are signed in as.
 *
 * Both used to sit in a bar above the page — a strip the console had and the
 * product does not, which is the first thing you notice moving between them.
 * Identity belongs with the navigation, and the page then starts at the top of
 * the window in both applications.
 */
export function SidebarFooter() {
  const { resolvedTheme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const name = user?.name || user?.email || 'Account';
  const row = cn(NAV_ROW, 'gap-2.5', navRowState(false));

  return (
    <div className="shrink-0 border-t border-sidebar-border p-2">
      <button
        type="button"
        onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        className={row}
      >
        <SunMoon className={cn(NAV_GLYPH, 'group-hover:rotate-45')} />
        <span className="flex-1 truncate text-left">Appearance</span>
        <span className="text-2xs text-muted-foreground">
          {resolvedTheme === 'dark' ? 'Dark' : 'Light'}
        </span>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={name}
            className={cn(
              'mt-1 flex w-full items-center gap-2.5 rounded-lg px-1.5 py-1.5 text-left transition-colors',
              'hover:bg-sidebar-accent/60 focus-visible:outline-2 focus-visible:outline-offset-2'
            )}
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="size-8 shrink-0 rounded-full" />
            ) : (
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary">
                <User className="size-4 text-muted-foreground" />
              </span>
            )}
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-sm font-semibold text-sidebar-foreground">
                {name}
              </span>
              <span className="block truncate text-2xs text-muted-foreground">{user?.email}</span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="top" align="start" sideOffset={8} className="w-56">
          <div className="mb-1 border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium text-foreground">{name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <DropdownMenuSeparator className="hidden" />
          <DropdownMenuItem onSelect={() => logout()} className="cursor-pointer text-destructive">
            <LogOut className="mr-2 size-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
